import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { Eye, EyeOff, Check } from "lucide-react"
import { FIREBASE_AUTH } from "@/firebase"
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth"
import { useAuthState } from "@/context/useAuthState"
import { toast } from "sonner"

const passwordChecks = (pw: string) => [
  { label: "6 caracteres minimo", ok: pw.length >= 6 },
  { label: "Una mayuscula", ok: /[A-Z]/.test(pw) },
  { label: "Un numero", ok: /\d/.test(pw) },
]

export default function SignupPage() {
  const navigate = useNavigate()
  const { setUser } = useAuthState()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const checks = passwordChecks(password)

  const handleGoogleSignup = async () => {
    setGoogleLoading(true)
    setError(null)
    try {
      const userCredential = await signInWithPopup(
        FIREBASE_AUTH,
        new GoogleAuthProvider()
      )
      setUser(userCredential.user)
      toast.success("Cuenta creada con Google")
      navigate("/")
    } catch (err: any) {
      if (err.code === "auth/popup-closed-by-user") return
      if (err.code === "auth/network-request-failed") {
        setError("Error de conexion. Verifique su internet.")
      } else {
        setError("Error al registrarse con Google")
      }
    } finally {
      setGoogleLoading(false)
    }
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    if (password !== confirmPassword) {
      setError("Las contrasenas no coinciden")
      setLoading(false)
      return
    }

    if (password.length < 6) {
      setError("La contrasena debe tener al menos 6 caracteres")
      setLoading(false)
      return
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        FIREBASE_AUTH,
        email,
        password
      )
      setUser(userCredential.user)
      toast.success("Cuenta creada exitosamente")
      navigate("/")
    } catch (err: any) {
      if (err.code === "auth/email-already-in-use") {
        setError("Este correo ya esta registrado. Intente iniciar sesion.")
      } else if (err.code === "auth/weak-password") {
        setError("La contrasena es muy debil.")
      } else if (err.code === "auth/invalid-email") {
        setError("El correo electronico no es valido.")
      } else if (err.code === "auth/network-request-failed") {
        setError("Error de conexion. Verifique su internet.")
      } else {
        setError("Error al registrarse")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex min-h-screen bg-white">
      {/* Left panel — brand */}
      <div className="relative hidden w-[52%] overflow-hidden bg-[#1F2240] lg:block">
        <img
          src="/inside.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-30 mix-blend-luminosity"
        />

        {/* Diagonal slash */}
        <div className="absolute -right-32 top-0 h-full w-64 skew-x-[-6deg] bg-white" />
        <div className="absolute -right-[8.5rem] top-0 h-full w-1 skew-x-[-6deg] bg-[#E10E19]" />

        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <Link to="/">
            <img
              src="/logo.png"
              alt="Oriental Ramirez"
              className="h-16 w-auto brightness-0 invert"
            />
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <h1 className="max-w-[16ch] font-[Kanit] text-5xl font-bold leading-[1.05] tracking-tight text-white">
              Crea tu cuenta y{" "}
              <span className="text-[#E10E19]">empieza hoy.</span>
            </h1>
            <p className="mt-4 max-w-[34ch] text-base leading-relaxed text-white/50">
              Cotiza, financia y recibe ofertas exclusivas desde tu perfil.
            </p>
          </motion.div>

          <p className="text-xs text-white/30">
            &copy; {new Date().getFullYear()} Oriental Ramirez. Todos los
            derechos reservados.
          </p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex flex-1 items-center justify-center overflow-y-auto px-6 py-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="w-full max-w-[420px]"
        >
          {/* Mobile logo */}
          <Link to="/" className="mb-10 flex items-center gap-3 lg:hidden">
            <img
              src="/logo.png"
              alt="Oriental Ramirez"
              className="h-12 w-auto"
            />
          </Link>

          <div className="mb-8">
            <h2 className="font-[Kanit] text-3xl font-bold tracking-tight text-[#1F2240]">
              Crear cuenta
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Registrate en Oriental Ramirez
            </p>
          </div>

          {/* Google button */}
          <button
            type="button"
            onClick={handleGoogleSignup}
            disabled={googleLoading || loading}
            className="group flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-all hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm disabled:opacity-50"
          >
            {googleLoading ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-[#E10E19]" />
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            )}
            Continuar con Google
          </button>

          <div className="my-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs font-medium uppercase tracking-widest text-gray-400">
              o con email
            </span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label
                htmlFor="signup-email"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                Email
              </label>
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="tu@email.com"
                className="h-12 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-[#E10E19]/30 focus:bg-white focus:ring-2 focus:ring-[#E10E19]/10 disabled:opacity-50"
              />
            </div>

            <div>
              <label
                htmlFor="signup-password"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                Contrasena
              </label>
              <div className="relative">
                <input
                  id="signup-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Minimo 6 caracteres"
                  className="h-12 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 pr-12 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-[#E10E19]/30 focus:bg-white focus:ring-2 focus:ring-[#E10E19]/10 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="h-4.5 w-4.5" />
                  ) : (
                    <Eye className="h-4.5 w-4.5" />
                  )}
                </button>
              </div>

              {/* Password strength indicators */}
              {password.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="mt-2 flex gap-3"
                >
                  {checks.map((c) => (
                    <span
                      key={c.label}
                      className={`flex items-center gap-1 text-[11px] transition-colors ${
                        c.ok ? "text-emerald-600" : "text-gray-400"
                      }`}
                    >
                      <Check
                        className={`h-3 w-3 ${c.ok ? "opacity-100" : "opacity-30"}`}
                      />
                      {c.label}
                    </span>
                  ))}
                </motion.div>
              )}
            </div>

            <div>
              <label
                htmlFor="signup-confirm"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500"
              >
                Confirmar contrasena
              </label>
              <input
                id="signup-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Repite la contrasena"
                className={`h-12 w-full rounded-xl border bg-gray-50/50 px-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:bg-white focus:ring-2 disabled:opacity-50 ${
                  confirmPassword.length > 0 && confirmPassword !== password
                    ? "border-red-300 focus:border-red-300 focus:ring-red-100"
                    : "border-gray-200 focus:border-[#E10E19]/30 focus:ring-[#E10E19]/10"
                }`}
              />
              {confirmPassword.length > 0 && confirmPassword !== password && (
                <p className="mt-1 text-xs text-red-500">
                  Las contrasenas no coinciden
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="relative h-12 w-full overflow-hidden rounded-xl bg-[#E10E19] font-[Kanit] text-sm font-semibold uppercase tracking-wider text-white transition-all hover:bg-[#C40D17] hover:shadow-lg hover:shadow-[#E10E19]/20 active:scale-[0.98] disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Registrando...
                </div>
              ) : (
                "Crear cuenta"
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-gray-500">
            Ya tienes cuenta?{" "}
            <Link
              to="/login"
              className="font-semibold text-[#E10E19] transition-colors hover:text-[#C40D17]"
            >
              Inicia sesion
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  )
}
